import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Ticket, Trash2, Loader2, Power } from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCoupons, useCreateCoupon, useUpdateCoupon, useDeleteCoupon } from '@/hooks/useCoupons';

export const Route = createFileRoute('/_authenticated/cupones')({
  component: CuponesPage,
});

const PRIZE_LABELS: Record<string, string> = {
  fichas: 'Fichas',
  bono100: 'Bono 100%',
  bono200: 'Bono 200%',
};

function CuponesPage() {
  const { data: coupons = [], isLoading } = useCoupons();
  const createMutation = useCreateCoupon();
  const updateMutation = useUpdateCoupon();
  const deleteMutation = useDeleteCoupon();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TopBanner title="Cupones" subtitle="Códigos promocionales canjeables por fichas o bono" />
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-4">
        <div className="text-sm text-muted-foreground">{coupons.length} cupón(es)</div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo cupón
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : coupons.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground">
            <Ticket className="mx-auto mb-2 h-8 w-8 opacity-50" />
            No hay cupones todavía. Creá el primero con "Nuevo cupón".
          </div>
        ) : (
          <div className="grid gap-3">
            {coupons.map((c) => (
              <Card key={c.id}>
                <CardContent className="flex flex-wrap items-center gap-4 p-4">
                  <code className="rounded bg-muted px-2 py-1 text-sm font-bold tracking-wide">{c.code}</code>
                  <Badge variant={c.prizeKind === 'fichas' ? 'default' : 'success'}>
                    {PRIZE_LABELS[c.prizeKind] ?? c.prizeKind} · {c.amount.toLocaleString('es-AR')}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Usos: {c.usedCount}{c.maxUses > 0 ? ` / ${c.maxUses}` : ' / ∞'}
                  </span>
                  {c.expiresAt && (
                    <span className="text-sm text-muted-foreground">
                      Vence: {new Date(c.expiresAt).toLocaleDateString('es-AR')}
                    </span>
                  )}
                  <Badge variant={c.isActive ? 'success' : 'secondary'}>
                    {c.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title={c.isActive ? 'Desactivar' : 'Activar'}
                      disabled={updateMutation.isPending}
                      onClick={() => updateMutation.mutate({ id: c.id, body: { isActive: !c.isActive } })}
                    >
                      <Power className={`h-4 w-4 ${c.isActive ? 'text-green-600' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-500"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(c.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <NewCouponDialog
        open={open}
        onOpenChange={setOpen}
        creating={createMutation.isPending}
        onCreate={(body) => createMutation.mutateAsync(body)}
      />
    </>
  );
}

function NewCouponDialog({
  open,
  onOpenChange,
  onCreate,
  creating,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreate: (body: {
    code: string;
    prizeKind: string;
    amount: number;
    maxUses: number;
    wagerMultiplier: number;
    expiresAt: string | null;
  }) => Promise<unknown>;
  creating: boolean;
}) {
  const [code, setCode] = useState('');
  const [prizeKind, setPrizeKind] = useState('fichas');
  const [amount, setAmount] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [wagerMultiplier, setWagerMultiplier] = useState('1');
  const [expiresAt, setExpiresAt] = useState('');

  const reset = () => {
    setCode(''); setPrizeKind('fichas'); setAmount(''); setMaxUses(''); setWagerMultiplier('1'); setExpiresAt('');
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!code.trim() || !amt || amt <= 0) return;
    await onCreate({
      code: code.trim().toUpperCase(),
      prizeKind,
      amount: amt,
      maxUses: Math.max(0, Number(maxUses) || 0),
      wagerMultiplier: Math.max(1, Number(wagerMultiplier) || 1),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo cupón</DialogTitle>
          <DialogDescription>El jugador escribe el código en el chat y se le acredita el premio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Código</label>
            <Input placeholder="FINDE100" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Premio</label>
              <Select value={prizeKind} onValueChange={setPrizeKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fichas">Fichas</SelectItem>
                  <SelectItem value="bono100">Bono 100%</SelectItem>
                  <SelectItem value="bono200">Bono 200%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Monto</label>
              <Input type="number" placeholder="5000" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Máx. usos (0 = ∞)</label>
              <Input type="number" placeholder="0" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
            </div>
            {prizeKind !== 'fichas' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Wager x</label>
                <Input type="number" placeholder="1" value={wagerMultiplier} onChange={(e) => setWagerMultiplier(e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Vencimiento (opcional)</label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={creating || !code.trim() || !amount}>
            {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear cupón
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
